import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, Pause, PhoneOutgoing, Play, Trash2 } from 'lucide-react';
import { apiDel, apiGet } from '../../lib/api.js';
import { fmtDate, fmtFromNow, fmtPhone } from '../../lib/format.js';
import { openRecordingStream } from '../../lib/recordings.js';

export const OUTBOUND_VM_KEY = ['phone-voicemails-outbound'];

/** @param {number} sec @returns {string} e.g. "0:38". */
export const clock = (sec) => `${Math.floor((sec || 0) / 60)}:${String(Math.round((sec || 0) % 60)).padStart(2, '0')}`;

/**
 * Hook: outbound voicemails left for leads (drops + voicemail dispositions).
 * @returns {import('@tanstack/react-query').UseQueryResult<{data: {voicemails: object[]}}>}
 */
export function useOutboundVoicemails() {
  return useQuery({ queryKey: OUTBOUND_VM_KEY, queryFn: () => apiGet('/phone/voicemails/outbound'), staleTime: 15_000 });
}

/**
 * Outbound voicemail rows: clickable lead name, number, drop used, date and
 * length, inline play/pause and delete. Audio lives in this component, so
 * opening a lead beside it never interrupts playback.
 * @param {Object} props
 * @param {(leadId: string) => void} [props.onOpenLead]
 * @returns {JSX.Element}
 */
export default function OutboundVoicemailList({ onOpenLead }) {
  const qc = useQueryClient();
  const { data, isLoading } = useOutboundVoicemails();
  const items = data?.data?.voicemails || [];
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  async function toggle(vm) {
    if (playingId === vm._id) { audioRef.current?.pause(); setPlayingId(null); return; }
    audioRef.current?.pause();
    setLoadingId(vm._id);
    try {
      // What was left: the drop's own audio, else the call recording through the proxy.
      const url = vm.dropAudioUrl || await openRecordingStream(vm.callId || vm._id);
      if (!url) { toast.error('No recording available for this voicemail'); return; }
      const audio = new Audio(url);
      audio.onended = () => setPlayingId(null);
      await audio.play();
      audioRef.current = audio;
      setPlayingId(vm._id);
    } catch {
      toast.error('Could not play this voicemail');
      setPlayingId(null);
    } finally {
      setLoadingId(null);
    }
  }

  const remove = useMutation({
    mutationFn: (id) => apiDel(`/phone/voicemails/outbound/${id}`),
    onSuccess: (_r, id) => {
      if (playingId === id) { audioRef.current?.pause(); setPlayingId(null); }
      qc.invalidateQueries({ queryKey: OUTBOUND_VM_KEY });
      toast.success('Voicemail removed');
    },
    onError: () => toast.error('Could not remove voicemail'),
  });

  if (isLoading) return <div className="text-center py-8 text-white/30 text-sm">Loading…</div>;
  if (items.length === 0) return <div className="text-center py-8 text-white/30 text-sm">No outbound voicemails yet</div>;

  return (
    <ul className="space-y-2">
      {items.map((vm) => {
        const name = [vm.lead?.firstName, vm.lead?.lastName].filter(Boolean).join(' ');
        const canPlay = vm.dropAudioUrl || vm.hasRecording;
        return (
          <li key={vm._id} className="bg-civix-panelLight rounded-xl p-3 border border-civix-border/60 space-y-1.5" data-testid="outbound-vm-row">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {vm.lead?._id ? (
                  <button
                    type="button"
                    onClick={() => onOpenLead?.(String(vm.lead._id))}
                    className="text-civix-purpleSoft hover:text-civix-cyan hover:underline cursor-pointer font-semibold text-sm truncate max-w-full text-left"
                  >
                    {name || 'Unnamed lead'}
                  </button>
                ) : (
                  <span className="text-white/70 text-sm font-semibold">Unknown lead</span>
                )}
                <div className="flex items-center gap-1.5 text-xs text-white/60">
                  <PhoneOutgoing className="w-3 h-3 text-civix-purple" /> {fmtPhone(vm.toNumber)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => { if (window.confirm('Remove this voicemail from your list?')) remove.mutate(vm._id); }}
                disabled={remove.isPending}
                className="text-white/30 hover:text-red-400 transition shrink-0"
                aria-label="Delete voicemail"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[11px] text-white/45" title={vm.createdAt ? fmtDate(vm.createdAt) : ''}>
              {vm.usedDrop ? `Drop: ${vm.dropName || 'Voicemail drop'}` : 'Left via call'} · {fmtFromNow(vm.createdAt)}
              {(vm.durationSec > 0) && ` · ${clock(vm.durationSec)}`}
            </div>
            {canPlay && (
              <button
                type="button"
                onClick={() => toggle(vm)}
                className="flex items-center gap-2 text-civix-purple text-xs hover:text-civix-purpleSoft transition"
              >
                {loadingId === vm._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : playingId === vm._id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                {playingId === vm._id ? 'Pause' : 'Play Recording'}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
